import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismModal, PrismSelectInput } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import GeneralEmptyStateOtbBanner from 'lumen/components/general/empty-state-otb-banner';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import can from 'lumen/helpers/can';
import hasFeature from 'lumen/helpers/has-feature';
import {
  OTB_SS_ENTRY_POINTS,
  OTB_SS_VIA_QP_VALUES,
} from 'lumen/utils/constants/otb-selfservice-constants';

const CALENDAR_COLOR_LIMIT_TYPES = {
  LOWER: 'calendarColorLowerLimit',
  UPPER: 'calendarColorUpperLimit',
};

const possibleLimitValues = [-50, -40, -30, -20, -10, 0, 10, 20, 30, 40, 50];

interface HotelRatesModalsCalendarSettingsModalSignature {
  Args: {
    closeModal: unknown;
  };
}

export default class HotelRatesModalsCalendarSettingsModal extends Component<HotelRatesModalsCalendarSettingsModalSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked calendarColorLowerLimitSelectedValue = null;
  @tracked calendarColorUpperLimitSelectedValue = null;

  saveCalendarSettingsTask = task({ drop: true }, async () => {
    if (this.haveCalendarSettingsChanged) {
      // Update the settings under hotel resource
      // @ts-expect-error: Incorrect type
      this.hotel.calendarColorLowerLimit =
        this.calendarColorLowerLimitSelectedValue;
      // @ts-expect-error: Incorrect type
      this.hotel.calendarColorUpperLimit =
        this.calendarColorUpperLimitSelectedValue;

      // Send a BE request to save the user changed settings
      // @ts-expect-error: Incorrect type
      await this.hotel.save();
    }

    // @ts-expect-error: Incorrect type
    this.args.closeModal();
  });

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get calendarColorLowerLimitOptions() {
    // @ts-expect-error: Incorrect type
    const returnArray = [];
    const lastLimit = possibleLimitValues[possibleLimitValues.length - 1];

    possibleLimitValues.forEach((current) => {
      // @ts-expect-error: Incorrect type
      if (current < lastLimit) {
        let labelValue = this.intl.t('rates.legend.competitor_price');
        if (current < 0) {
          labelValue = this.intl.t('rates.legend.percentage_lower', {
            count: -current,
          });
        } else if (current > 0) {
          labelValue = this.intl.t('rates.legend.percentage_higher', {
            count: current,
          });
        }
        returnArray.push({
          label: labelValue,
          value: current,
        });
      }
    });
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return returnArray;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get calendarColorLowerLimitSelectedOption() {
    let selectedObject = null;
    const selected = this.calendarColorLowerLimitSelectedValue;
    this.calendarColorLowerLimitOptions.forEach((current) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (current.value === selected) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        selectedObject = current;
      }
    });
    return selectedObject;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get calendarColorUpperLimitOptions() {
    // @ts-expect-error: Incorrect type
    const returnArray = [];
    const lowerLimit = this.calendarColorLowerLimitSelectedValue;

    possibleLimitValues.forEach((current) => {
      // @ts-expect-error: Incorrect type
      if (current > lowerLimit) {
        let labelValue = this.intl.t('rates.legend.competitor_price');
        if (current < 0) {
          labelValue = this.intl.t('rates.legend.percentage_lower', {
            count: -current,
          });
        } else if (current > 0) {
          labelValue = this.intl.t('rates.legend.percentage_higher', {
            count: current,
          });
        }
        returnArray.push({
          label: labelValue,
          value: current,
        });
      }
    });
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return returnArray;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get calendarColorUpperLimitSelectedOption() {
    let selectedObject = null;
    if (
      // @ts-expect-error: Incorrect type
      this.calendarColorUpperLimitSelectedValue <=
      // @ts-expect-error: Incorrect type
      this.calendarColorLowerLimitSelectedValue
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      selectedObject = this.calendarColorUpperLimitOptions?.[0];
    } else {
      const selectedCalendarColorUpperLimit =
        this.calendarColorUpperLimitSelectedValue;
      this.calendarColorUpperLimitOptions.forEach((option) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (option.value === selectedCalendarColorUpperLimit) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          selectedObject = option;
        }
      });
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return selectedObject;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get haveCalendarSettingsChanged() {
    return (
      this.hotel?.calendarColorLowerLimit !==
        this.calendarColorLowerLimitSelectedValue ||
      this.hotel?.calendarColorUpperLimit !==
        this.calendarColorUpperLimitSelectedValue
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isConfirmDisabled() {
    return (
      this.saveCalendarSettingsTask.isRunning ||
      !this.haveCalendarSettingsChanged
    );
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // @ts-expect-error: Incorrect type
    this.calendarColorLowerLimitSelectedValue =
      this.hotel?.calendarColorLowerLimit;
    // @ts-expect-error: Incorrect type
    this.calendarColorUpperLimitSelectedValue =
      this.hotel?.calendarColorUpperLimit;
  }

  @action
  // @ts-expect-error: Incorrect type
  limitChanged(limit, option): void {
    if (limit === CALENDAR_COLOR_LIMIT_TYPES.LOWER) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.calendarColorLowerLimitSelectedValue = option.value;
    } else if (limit === CALENDAR_COLOR_LIMIT_TYPES.UPPER) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.calendarColorUpperLimitSelectedValue = option.value;
    }

    // Adjust selected upper limit if it's lower than the lower limit
    this.calendarColorUpperLimitSelectedValue =
      // @ts-expect-error: Incorrect type
      this.calendarColorUpperLimitSelectedValue <=
      // @ts-expect-error: Incorrect type
      this.calendarColorLowerLimitSelectedValue
        ? this.calendarColorLowerLimitSelectedValue
        : this.calendarColorUpperLimitSelectedValue;

    if (
      !this.calendarColorUpperLimitOptions
        .map(
          (calendarColorUpperLimitOption) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
            calendarColorUpperLimitOption.value,
        )
        .includes(this.calendarColorUpperLimitSelectedValue)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.calendarColorUpperLimitSelectedValue =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (this.calendarColorUpperLimitOptions ?? [])[0]?.value;
    }
  }

  @action
  onUpdateCalendarSettings(): void {
    this.usageAnalytics.track('Calendar Settings Changed', {
      eventTrigger: 'calendar-settings-modal',
      product: 'pricing-manager',
    });

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.saveCalendarSettingsTask.perform();
  }

  <template>
    {{! @glint-expect-error: Incorrect type }}
    <PrismModal @onClose={{@closeModal}} @title={{t "rates.calendar_settings"}}>
      <div
        class="rates-calendar-settings-modal modal--small modal--padded flex flex-col items-center justify-center"
      >
        {{#if (hasFeature "otb.empty_placeholder")}}
          <GeneralEmptyStateOtbBanner
            @isDismissable={{false}}
            @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.RATES_CALENDAR_SETTINGS_MODAL}}
            @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.RATE_INSIGHT}}
          />
          <div class="divider-line"></div>
        {{/if}}

        <div class="competitor-price-selector-wrapper">
          <span class="text-200--bold">{{t
              "rates.calendar_settings.competitor_price.title"
            }}</span>
          <div class="competitor-price-visualisations mt-200">
            <div class="flex flex-row">
              <div class="green competitor-price-visualisation-col"></div>
              <div class="competitor-price-visualisation-divider"></div>
              <div class="orange competitor-price-visualisation-col"></div>
              <div class="competitor-price-visualisation-divider"></div>
              <div class="red competitor-price-visualisation-col"></div>
            </div>
            <div class="competitor-price-visualisations__triggers">
              <div class="competitor-price-visualisation-trigger">
                {{#if (can "settings.calendar.colorlimits")}}
                  <PrismSelectInput
                    @isSmall={{true}}
                    @onChange={{fn
                      this.limitChanged
                      CALENDAR_COLOR_LIMIT_TYPES.LOWER
                    }}
                    @options={{this.calendarColorLowerLimitOptions}}
                    @value={{this.calendarColorLowerLimitSelectedOption}}
                  />
                {{else}}
                  <div
                    class="competitor-price-visualisation-trigger--read-only"
                  >
                    {{! @glint-expect-error: Incorrect type }}
                    {{this.calendarColorLowerLimitSelectedOption.label}}
                  </div>
                {{/if}}
              </div>
              <div class="competitor-price-visualisation-trigger">
                {{#if (can "settings.calendar.colorlimits")}}
                  <PrismSelectInput
                    @isSmall={{true}}
                    @onChange={{fn
                      this.limitChanged
                      CALENDAR_COLOR_LIMIT_TYPES.UPPER
                    }}
                    @options={{this.calendarColorUpperLimitOptions}}
                    @value={{this.calendarColorUpperLimitSelectedOption}}
                  />
                {{else}}
                  <div
                    class="competitor-price-visualisation-trigger--read-only"
                  >
                    {{this.calendarColorUpperLimitSelectedOption.label}}
                  </div>
                {{/if}}
                <div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="settings-modal__footer">
        <GeneralFormFieldsCancelAndConfirm
          {{! @glint-expect-error: Incorrect type }}
          @cancel={{@closeModal}}
          @confirm={{this.onUpdateCalendarSettings}}
          @confirmText={{t "general.apply"}}
          @isConfirmDisabled={{this.isConfirmDisabled}}
        />
      </div>
    </PrismModal>
  </template>
}
